/**
 * Created by neo on 23.12.22.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Translation } from '../../Components/Translation';
import { useTrans } from '../../Store/System/LocalizationStore';
import { Link } from 'react-router-dom';


const ContentContainer = styled.div`
  padding: 48px 24px;
  background: #f8faf9;
  border-radius: 20px;
  border: 1px solid rgba(0, 125, 99, 0.08);

  @media (min-width: 768px) {
    padding: 56px 48px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 32px;
  max-width: 1100px;
  margin: 0 auto;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 48px;
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const ImageWrapper = styled.div`
  position: relative;
`;

const Image = styled.img`
  height: 200px;
  object-fit: contain;

  @media (min-width: 768px) {
    height: 280px;
  }
`;

const TextContent = styled.div`
  flex: 1.2;
  text-align: center;

  @media (min-width: 768px) {
    text-align: left;
  }
`;

const Badge = styled.span`
  display: inline-block;
  background: #00dda2;
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 5px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
`;

const Title = styled.h3`
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 16px 0;
  color: #1a1a2e;

  @media (min-width: 768px) {
    font-size: 2.25rem;
  }
`;

const AccentText = styled.span`
  color: #00dda2;
`;

const Description = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: #555;
  margin: 0 0 28px 0;
  max-width: 480px;

  @media (min-width: 768px) {
    font-size: 1.1rem;
  }

  @media (max-width: 767px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const StyledButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #00dda2;
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s ease;

  &:hover {
    background: #007763;
    color: white;
    text-decoration: none;
  }
`;

const ArrowIcon = styled.span`
  font-size: 1.1rem;
  transition: transform 0.3s ease;
`;

export type NutritionLibrarySectionProps = {};

export const NutritionLibrarySection: React.FC<NutritionLibrarySectionProps> = observer(() => {
  const { t } = useTrans();

  return (
    <ContentContainer>
      <ContentWrapper>
        <ImageContainer>
          <ImageWrapper>
            <Image src={require('../../Assets/Images/nutrition_library_section.png')} alt="Nutrition" />
          </ImageWrapper>
        </ImageContainer>
        <TextContent>
          <Badge>{t('nutritionScreen.librarySection.badge', { defaultValue: 'Recipe Collection' })}</Badge>
          <Title>
            {t('nutritionScreen.librarySection.titleStart', { defaultValue: 'Browse All' })}{' '}
            <AccentText>{t('nutritionScreen.librarySection.titleAccent', { defaultValue: 'Recipes' })}</AccentText>
          </Title>
          <Description>
            <Translation
              i18nKey="nutritionScreen.librarySection.description"
              defaultValue="Explore our entire collection of healthy recipes. Filter by preparation time, ingredients, and dietary preferences to find your perfect meal."
            />
          </Description>
          <StyledButton to="library">
            {t('nutritionScreen.librarySection.goToLibraryButton', { defaultValue: 'Explore Library' })}
          </StyledButton>
        </TextContent>
      </ContentWrapper>
    </ContentContainer>
  );
});
